import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { atom, useAtom, useSetAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import { useTracker } from '@archipro-website/tracker';
import { trackEnquiryEngagement } from '~/modules/tracking/util/trackEnquiryEngagement';
import { trackEnquireReveal } from '~/modules/tracking/util/trackEnquireReveal';
import { trackProjectEnquire } from '~/modules/tracking/util/trackProjectEnquiry';
import {
    ENQUIRE_AUTH_SOURCE_VALUE,
    ENQUIRE_MODAL_SHOWUP_KEY,
    ENQUIRE_MODAL_SHOWUP_KEY_VALUE as KEY_VALUE,
} from '~/modules/enquiry/constants';
import {
    AUTH_PROVIDER_PARAM_KEY,
    AUTH_SOURCE_PARAM_KEY,
} from '~/modules/root/hook/use-auth-event';
import {
    enquirySessionAtom,
    type EnquiryProfessional,
    type UseEnquiryProps,
} from './use-enquiry';
import { getDefaultEnquiryMessage } from '../util/get-default-enquiry-message';

export type EnquiryModalProfessional = EnquiryProfessional & {
    CompanyLogo: string;
    LogoBackgroundColor: string;
    CustomEnquiryMessage?: string;
    EnquiryMessageOverrideEnabled?: boolean;
    EnquiryMessagePlaceholder?: string;
};

interface EnquiryModalContext {
    /** The title of the modal. */
    title?: string;
    /** The professional to submit an enquiry to. */
    professional: EnquiryModalProfessional;
    /** The type of the Library Item (professional, product, project, article). */
    itemType: UseEnquiryProps['itemType'];
    /** The ID of the Library Item (professional, product, project, article). */
    siteTreeID: UseEnquiryProps['siteTreeID'];
    /** The source of the enquiry. Defaults to `EnquiryModal`. */
    source?: Exclude<UseEnquiryProps['source'], 'InPage'>;
    /** The project details if the enquiry is for a project. Used for tracking. */
    relatedItem?: UseEnquiryProps['relatedItem'];
    /** The CTA text that opened the modal. Used for tracking. */
    ctaLabel?: string;
    /** Include the branch dropdown if the professional has branches. Defaults to `false`. */
    includeBranch?: boolean;
    /** The project ID if the enquiry is for a project. Used for tracking. */
    projectID?: number;
    /** The name of the item being enquired about (e.g. product name). Shown as subtitle in the modal header. */
    itemTitle?: string;
    /** Overrides `itemTitle` for message generation only (e.g. a specific file name). */
    messageSubject?: string;
    /** A thumbnail URL for the item being enquired about. Shown in the modal header. */
    itemImage?: string;
    /**
     * A high-quality background for the gate's brand pane (product image,
     * professional hero banner, project banner). Falls back to `itemImage`,
     * then the default brand imagery.
     */
    brandImage?: string;
    /**
     * True while the professional's details are still being resolved, so the
     * header shows a logo skeleton rather than an empty tile. Only surfaces that
     * open the modal before their context is loaded set this — a professional
     * with no logo must leave it `false` or the shimmer never resolves.
     */
    logoPending?: boolean;
}

interface UseEnquiryModalOptions {
    /**
     * Open the modal on mount when the `enquire` URL param is present. Defaults
     * to `true`. Surfaces that mount many instances of the same hook on one page
     * (e.g. every tile in a grid) must opt out, or they all race to auto-open
     * with their own context.
     */
    autoOpenFromUrl?: boolean;
}

/**
 * The auto-filled message a context would open with, matching what
 * `EnquiryGateModal` passes to `useEnquiry` (`messageSubject` standing in for
 * `itemTitle`). Used to tell an untouched default apart from a real draft.
 */
const defaultMessageFor = (context: EnquiryModalContext | null): string =>
    context
        ? getDefaultEnquiryMessage({
              itemType: context.itemType,
              itemTitle: context.messageSubject ?? context.itemTitle,
              ctaLabel: context.ctaLabel,
              professional: context.professional,
          })
        : '';

/**
 * Whether reopening the modal with `next` should drop the saved draft.
 *
 * Three reasons to drop it: a different item, a different subject (a new
 * variant), or a CTA that asks a different question. The last one matters
 * because one product can carry two enquiry CTAs — "Request a Sample" and
 * "Request Price / Brochure" — whose generated copy differs while the subject
 * does not, and the saved draft otherwise wins over the new default and shows
 * the previous CTA's wording.
 *
 * A message the reader has typed into no longer equals the default it started
 * from, so it survives a CTA change; only the subject and item rules override
 * a real draft.
 */
export const shouldClearDraftMessage = ({
    previous,
    next,
    draftMessage,
    draftSiteTreeId,
    messageSubject,
}: {
    /** Context the modal last opened with, if any. */
    previous: EnquiryModalContext | null;
    /** Context it is opening with now. */
    next: EnquiryModalContext;
    /** The saved draft body. */
    draftMessage?: string;
    /** The item the saved draft belongs to. */
    draftSiteTreeId?: number;
    /** `messageSubject` as passed to this open call, before it is merged. */
    messageSubject?: string;
}): boolean => {
    if (draftSiteTreeId !== next.siteTreeID) {
        return true;
    }
    if (
        messageSubject !== undefined &&
        messageSubject !== previous?.messageSubject
    ) {
        return true;
    }
    return (
        !!draftMessage &&
        draftMessage === defaultMessageFor(previous) &&
        draftMessage !== defaultMessageFor(next)
    );
};

export const enquiryModalOpenAtom = atom(false);
const enquiryModalContextAtom = atomWithStorage<EnquiryModalContext | null>(
    'enquiry-modal-context',
    null,
    createJSONStorage(() => sessionStorage),
);

/**
 * Pathname whose `EnquiryEngagement` has already been reported.
 *
 * The contract is "max once per page view", but the guard used to be a ref on
 * each hook instance — and the revamp mounts this hook nine times on a product
 * page and twice on an article, so the page reported engagement once per
 * mount. Shared state is the only thing that can enforce a page-wide rule.
 * An atom rather than a module variable so it cannot leak across requests
 * during SSR.
 */
const enquiryEngagementPathAtom = atom<{
    pathname: string;
    reported: Set<string>;
} | null>(null);

/**
 * What counts as one engagement: a surface on a page, for one professional.
 *
 * Keying on the pathname alone collapsed the whole page to a single report, so
 * the per-CTA `source` values threaded through this hook were unmeasurable
 * after the first touch — and a project crediting three practices reported
 * engagement with one of them.
 */
const engagementKey = (source: string, professionalID: number): string =>
    `${source}:${professionalID}`;

/**
 * Hook to interact with the Enquiry Modal. Can be used to render an Enquiry
 * Modal or open it. Components opening the modal should pass in data about the
 * enquiry (professional, etc.) for the modal to render.
 * @param data Context about the enquiry for the modal to render.
 * @param options Behavioural overrides for this instance.
 */
export function useEnquiryModal(
    data?: EnquiryModalContext,
    { autoOpenFromUrl = true }: UseEnquiryModalOptions = {},
) {
    const isAutoOpened = useRef(false);
    const [engagementTracked, setEngagementTracked] = useAtom(
        enquiryEngagementPathAtom,
    );
    const [isOpen, setIsOpen] = useAtom(enquiryModalOpenAtom);
    const [context, setContext] = useAtom(enquiryModalContextAtom);
    const setEnquirySession = useSetAtom(enquirySessionAtom);
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const tracker = useTracker();

    const openEnquiryModal = useCallback(
        ({
            title,
            ctaLabel,
            messageSubject,
            itemImage,
            brandImage,
            addQueryParam = false,
            autoOpened = false,
        }: {
            title?: string;
            ctaLabel?: string;
            /** Override the subject used to generate the default enquiry message (e.g. a file name). */
            messageSubject?: string;
            /** Override the header thumbnail (e.g. the selected variant's image). */
            itemImage?: string;
            /**
             * Override the gate's brand-pane image. Needed alongside
             * `itemImage` because the pane prefers `brandImage`, so a variant
             * that only replaced the thumbnail would still show the base
             * product behind the gate.
             */
            brandImage?: string;
            addQueryParam?: boolean;
            /** True when opened from the `enquire` URL param rather than a click on this instance's surface. */
            autoOpened?: boolean;
        } = {}) => {
            const newContext = data
                ? {
                      ...data,
                      // A URL-triggered open has no clicked surface, and every
                      // mounted instance auto-opens, so keep the default source
                      // rather than stamping this instance's surface on the
                      // reveal events and the persisted context.
                      ...(autoOpened && { source: 'EnquiryModal' as const }),
                      ...(title && { title }),
                      ...(ctaLabel && { ctaLabel }),
                      ...(messageSubject && { messageSubject }),
                      ...(itemImage && { itemImage }),
                      ...(brandImage && { brandImage }),
                  }
                : context;
            if (!newContext) {
                return;
            }
            setEnquirySession((prev) => {
                if (!prev) return prev;
                return shouldClearDraftMessage({
                    previous: context,
                    next: newContext,
                    draftMessage: prev.message,
                    draftSiteTreeId: prev.siteTreeId,
                    messageSubject,
                })
                    ? { ...prev, message: undefined }
                    : prev;
            });
            const { professional, itemType, siteTreeID, source } = newContext;
            if (data) {
                // Only update context if data is provided.
                setContext(newContext);
            }
            setIsOpen(true);
            isAutoOpened.current = true;

            if (addQueryParam) {
                searchParams.set(ENQUIRE_MODAL_SHOWUP_KEY, KEY_VALUE);
                setSearchParams(searchParams, { preventScrollReset: true });
            }

            trackEnquireReveal(tracker, {
                professionalName: professional.Title,
                itemID: siteTreeID,
                itemType,
                source: source ?? 'EnquiryModal',
                ctaLabel: newContext.ctaLabel,
                projectID: newContext.projectID,
                professionalID: professional.ID,
            });
            if (itemType === 'project' && newContext.relatedItem) {
                trackProjectEnquire(tracker, {
                    category: newContext.relatedItem.category,
                    step: 1,
                    project: newContext.relatedItem.title,
                    professional: professional.Title,
                });
            }
            // Once per surface per professional per page view, across every
            // mounted instance of this hook — the revamp mounts it nine times
            // on a product page, so a per-instance guard counted one reader as
            // nine, and a page-wide one counted nine surfaces as one.
            const resolvedSource = source ?? 'EnquiryModal';
            const key = engagementKey(resolvedSource, professional.ID);
            const samePage = engagementTracked?.pathname === location.pathname;
            if (!samePage || !engagementTracked.reported.has(key)) {
                trackEnquiryEngagement(tracker, {
                    professional: {
                        ID: professional.ID,
                        Title: professional.Title,
                    },
                    itemID: siteTreeID,
                    itemType,
                    source: resolvedSource,
                });
                setEngagementTracked({
                    pathname: location.pathname,
                    reported: new Set(
                        samePage ? [...engagementTracked.reported, key] : [key],
                    ),
                });
            }
        },
        [
            tracker,
            searchParams,
            data,
            context,
            setIsOpen,
            setContext,
            setSearchParams,
            setEnquirySession,
            engagementTracked,
            setEngagementTracked,
            location.pathname,
        ],
    );

    /**
     * Replace the open modal's context with this instance's latest `data`,
     * without re-opening it or re-firing the reveal tracking. For surfaces that
     * open with partial context and enrich it in the background.
     */
    const refreshEnquiryModalContext = useCallback(() => {
        if (!data) {
            return;
        }
        setContext(data);
    }, [data, setContext]);

    useEffect(() => {
        // Open the enquiry modal on page load if query param is present.
        if (!autoOpenFromUrl) {
            return;
        }
        if (searchParams.get(ENQUIRE_MODAL_SHOWUP_KEY) !== KEY_VALUE) {
            return;
        }
        // Redirect after social login from enquiry modal, do nothing.
        if (
            searchParams.has(AUTH_PROVIDER_PARAM_KEY) &&
            searchParams.get(AUTH_SOURCE_PARAM_KEY) ===
                ENQUIRE_AUTH_SOURCE_VALUE
        ) {
            return;
        }
        // isAutoOpened is required to prevent infinite loop.
        if (!isAutoOpened.current && data) {
            openEnquiryModal({ autoOpened: true });
        }
    }, [autoOpenFromUrl, searchParams, data, openEnquiryModal]);

    useEffect(() => {
        if (!data) return;
        const urls = [data.professional.CompanyLogo, data.itemImage].filter(
            (url): url is string => Boolean(url),
        );
        urls.forEach((url) => {
            const img = new window.Image();
            img.src = url;
        });
    }, [data]);

    useEffect(() => {
        isAutoOpened.current = false;
    }, [location.pathname]);

    return {
        /** The open state of the enquiry modal. */
        isOpen,
        /** The current context of the enquiry modal (e.g. professional, item type, etc.). */
        context,
        /**
         * Open the enquiry modal.
         * @param title An optional title of the modal.
         * @param ctaLabel An optional CTA text that opened the modal. Used for tracking.
         * @param addQueryParam Add the query param to the URL so the modal is
         *   auto opened on page load. Defaults to `false`.
         */
        openEnquiryModal,
        /**
         * Update the open modal's context from this instance's `data` without
         * re-opening it or re-tracking the reveal.
         */
        refreshEnquiryModalContext,
        /** Close the enquiry modal. */
        closeEnquiryModal: useCallback(() => {
            if (searchParams.has(ENQUIRE_MODAL_SHOWUP_KEY)) {
                searchParams.delete(ENQUIRE_MODAL_SHOWUP_KEY);
                setSearchParams(searchParams, { preventScrollReset: true });
            }
            setIsOpen(false);
        }, [searchParams, setSearchParams, setIsOpen]),
    };
}
